// client/src/pages/pmPortal/OwnerStatementPage.js
// The same figures the office sees, from the same query in lib/pmStatements.js. Two
// implementations would eventually disagree about one month and nobody would know which.
import React, { useCallback, useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Input, Select, money }
    from '../../components/secure/pm/PMKit';

const thisYear = new Date().getFullYear();

const SECTION_LABELS = {
    income: 'Income',
    operating_expense: 'Operating expenses',
    capital: 'Capital items',
    trust: 'Trust movements',
    equity: 'Equity',
    other: 'Other',
};

const OwnerStatementPage = () => {
    const api = usePmPortalApiClient();
    const [from, setFrom] = useState(`${thisYear}-01-01`);
    const [to, setTo] = useState(`${thisYear}-12-31`);
    const [propertyId, setPropertyId] = useState('');
    const [properties, setProperties] = useState([]);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/owner/summary')
            .then(d => setProperties(d.properties || []))
            .catch(() => { /* the statement itself reports any failure */ });
    }, [api]);

    const load = useCallback(() => {
        const params = new URLSearchParams({ from, to });
        if (propertyId) params.set('property_id', propertyId);
        setError('');
        setData(null);
        api.get(`/api/pm-portal/owner/statement?${params}`)
            .then(setData)
            .catch(e => setError(e.message));
    }, [api, from, to, propertyId]);

    useEffect(() => { load(); }, [load]);

    return (
        <>
            <PageHeader
                title="Statement"
                subtitle="Income and expenses on your book for the period. Rent is shown as it was earned, not as it was banked."
            />

            <Panel title="Period">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Input label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} />
                    <Input label="To" type="date" value={to} onChange={e => setTo(e.target.value)} />
                    <Select
                        label="Property"
                        value={propertyId}
                        onChange={e => setPropertyId(e.target.value)}
                        options={[
                            { value: '', label: 'All properties' },
                            ...properties.map(p => ({ value: p.pm_property_id, label: p.property_name })),
                        ]}
                    />
                </div>
            </Panel>

            {error && <Notice tone="bad" title="Could not build the statement" message={error} />}
            {!data && !error && <Loading />}

            {data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                        <Stat label="Income" value={money(data.totals.income)} />
                        <Stat label="Operating expenses" value={money(data.totals.operating_expense)} />
                        <Stat label="Net operating income" value={money(data.totals.net_operating_income)} />
                        <Stat
                            label="Cash flow"
                            value={money(data.totals.cash_flow)}
                            sub="after capital items"
                            tone={Number(data.totals.cash_flow) < 0 ? 'bad' : 'default'}
                        />
                    </div>

                    {Object.entries(SECTION_LABELS).map(([key, label]) => {
                        const rows = data.sections[key] || [];
                        if (!rows.length) return null;
                        return (
                            <Panel key={key} title={label}>
                                <Table
                                    columns={[
                                        { key: 'code', label: 'Account' },
                                        { key: 'name', label: 'Description' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => money(r.amount) },
                                    ]}
                                    rows={rows}
                                />
                            </Panel>
                        );
                    })}
                </>
            )}
        </>
    );
};

export default OwnerStatementPage;
